import React, { useState, useEffect } from 'react';
import { X, FileText, CheckCircle } from 'lucide-react';
import api from '../../services/api';

interface SelectedRow {
  id: number;
  vendor_id: number;
  vendor_name: string;
  itinerary_id: number;
  guest_name: string;
  item_names: string;
  item_count: number;
  item_ids: number[];
  outstanding_amount: number;
}

interface BulkPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRows: SelectedRow[];
  onSuccess: () => void;
}

const BulkPaymentModal: React.FC<BulkPaymentModalProps> = ({
  isOpen,
  onClose,
  selectedRows,
  onSuccess
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [outboundAccounts, setOutboundAccounts] = useState<string[]>([]);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [bankAccount, setBankAccount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const totalAmount = selectedRows.reduce((sum, r) => sum + (r.outstanding_amount || 0), 0);
  const vendorName = selectedRows[0]?.vendor_name || '';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setBankAccount('');
    setPaymentReference('');
    setNotes('');
    setReceiptFile(null);
    setError('');

    const loadOutboundAccounts = async () => {
      try {
        const response = await api.get('/treasury/accounts?account_role=outbound');
        if (response.data.success) {
          setOutboundAccounts(response.data.data.map((acc: any) => acc.name));
        }
      } catch (err) {
        console.error('Error loading outbound accounts:', err);
      }
    };
    loadOutboundAccounts();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleContinue = () => {
    if (!bankAccount) {
      setError('Please select a payment channel');
      return;
    }
    if (!receiptFile) {
      setError('Please attach the payment proof (receipt file)');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      const itemIds = selectedRows.flatMap(r => r.item_ids);
      formData.append('item_ids', JSON.stringify(itemIds));
      formData.append('payment_amount', totalAmount.toString());
      formData.append('payment_date', paymentDate);
      formData.append('payment_reference', paymentReference);
      formData.append('bank_account', bankAccount);
      formData.append('notes', notes);
      if (receiptFile) {
        formData.append('receipt_file', receiptFile);
      }

      const response = await api.post('/accounts-v3/bulk-add-payment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      console.error('Error recording bulk payment:', err);
      setError(err?.response?.data?.detail || 'Error recording payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b">
          <div>
            <h2 className="text-lg sm:text-xl font-bold">Mark Payment Done — {vendorName}</h2>
            <p className="text-sm text-gray-600">
              {selectedRows.length} itinerary group{selectedRows.length > 1 ? 's' : ''} selected
              {step === 2 ? ' · Review before confirming' : ''}
            </p>
          </div>
          <button onClick={onClose} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
            ✕ Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                <input
                  type="text"
                  value={formatCurrency(totalAmount)}
                  readOnly
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                />
                <p className="text-xs text-gray-500 mt-1">Sum of outstanding amounts for the selected items</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Channel</label>
                <select
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select payment channel *</option>
                  {outboundAccounts.map((account) => (
                    <option key={account} value={account}>{account}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Bank transfer ref, check number, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Receipt File <span className="text-red-600">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {receiptFile && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                    <FileText className="h-4 w-4" />
                    <span>{receiptFile.name}</span>
                    <button onClick={() => setReceiptFile(null)} className="text-red-600 hover:text-red-800">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Proof of payment is required to mark items as paid</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div><strong>Total Payment:</strong> {formatCurrency(totalAmount)}</div>
                  <div><strong>Payment Date:</strong> {new Date(paymentDate).toLocaleDateString('en-GB')}</div>
                  <div><strong>Payment Channel:</strong> {bankAccount}</div>
                  <div><strong>Reference:</strong> {paymentReference || '—'}</div>
                  <div className="flex items-center gap-1">
                    <strong>Receipt:</strong>
                    <FileText className="h-4 w-4 text-green-700" />
                    <span>{receiptFile?.name}</span>
                  </div>
                  {notes && <div className="sm:col-span-2"><strong>Notes:</strong> {notes}</div>}
                </div>
              </div>

              <h3 className="text-sm font-medium text-gray-700 mb-2">Items being marked as paid</h3>
              <div className="space-y-2">
                {selectedRows.map((row) => (
                  <div key={row.id} className="bg-white border border-gray-200 rounded-lg p-3 flex justify-between items-center gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        WH_{row.itinerary_id} — {row.guest_name}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {row.item_count} item{row.item_count > 1 ? 's' : ''} · {row.item_names}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(row.outstanding_amount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 sm:p-6 border-t">
          {step === 1 && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleContinue}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Continue to Preview
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={submitting}
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                <CheckCircle className="h-4 w-4" />
                {submitting ? 'Recording Payment...' : 'Confirm & Mark Paid'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPaymentModal;
